import { useState, useEffect } from 'react';
import { Settings, Key, Mail, CheckCircle, XCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';

const OAuthSettings = () => {
  const [config, setConfig] = useState({
    google: { clientId: '', hasSecret: false, hasRefreshToken: false, enabled: false },
    microsoft: { clientId: '', tenantId: '', hasSecret: false, hasRefreshToken: false, enabled: false },
    emailProvider: 'none'
  });
  const [googleSecret, setGoogleSecret] = useState('');
  const [microsoftSecret, setMicrosoftSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/oauth/config', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to load OAuth config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveGoogleConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/oauth/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          provider: 'google',
          clientId: config.google.clientId,
          clientSecret: googleSecret || undefined,
          enabled: config.google.enabled
        })
      });
      
      if (response.ok) {
        toast.success('Google OAuth settings saved');
        setGoogleSecret('');
        loadConfig();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveMicrosoftConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/oauth/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          provider: 'microsoft',
          clientId: config.microsoft.clientId,
          clientSecret: microsoftSecret || undefined,
          tenantId: config.microsoft.tenantId,
          enabled: config.microsoft.enabled
        })
      });
      
      if (response.ok) {
        toast.success('Microsoft OAuth settings saved');
        setMicrosoftSecret('');
        loadConfig();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const connectEmailGoogle = () => {
    window.location.href = '/api/oauth/email/google';
  };

  const connectEmailMicrosoft = () => {
    window.location.href = '/api/oauth/email/microsoft';
  };

  const testEmail = async () => {
    try {
      const response = await fetch('/api/oauth/email/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Test email sent!');
      } else {
        toast.error(data.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  if (loading) {
    return <div>Loading OAuth settings...</div>;
  }

  return (
    <div className="oauth-settings">
      <h3 className="card-title">
        <Key size={20} />
        OAuth / SSO Settings
      </h3>
      <p className="oauth-settings-description">
        Configure OAuth providers for user login/registration and email sending.
      </p>

      {/* Google OAuth */}
      <div className="oauth-provider-card">
        <div className="oauth-provider-header">
          <div className="oauth-provider-title">
            <img src="https://www.google.com/favicon.ico" alt="Google"  />
            <h4>Google OAuth</h4>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={config.google.enabled}
              onChange={(e) => setConfig({ ...config, google: { ...config.google, enabled: e.target.checked } })}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Client ID</label>
            <input
              type="text"
              className="form-input"
              value={config.google.clientId}
              onChange={(e) => setConfig({ ...config, google: { ...config.google, clientId: e.target.value } })}
              placeholder="Google OAuth Client ID"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Client Secret {config.google.hasSecret && <span className="config-status">(configured)</span>}
            </label>
            <input
              type="password"
              className="form-input"
              value={googleSecret}
              onChange={(e) => setGoogleSecret(e.target.value)}
              placeholder={config.google.hasSecret ? '••••••••' : 'Enter client secret'}
            />
          </div>
        </div>

        <div className="oauth-form-actions">
          <button className="btn btn-primary btn-sm" onClick={saveGoogleConfig} disabled={saving}>
            Save Google Settings
          </button>
          
          {config.google.enabled && config.google.hasSecret && (
            <button className="btn btn-secondary btn-sm" onClick={connectEmailGoogle}>
              <Mail size={14} />
              {config.google.hasRefreshToken ? 'Reconnect' : 'Connect'} Email
            </button>
          )}
          
          {config.google.hasRefreshToken && (
            <span className="email-connected">
              <CheckCircle size={16} /> Email Connected
            </span>
          )}
        </div>

        <div className="oauth-help-links">
          <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="oauth-help-link">
            <ExternalLink size={12} /> Google Cloud Console
          </a>
          <span className="callback-url">
            Callback URL: <code >{window.location.origin}/api/auth/google/callback</code>
          </span>
        </div>
      </div>

      {/* Microsoft OAuth */}
      <div className="oauth-provider-card mt-24">
        <div className="oauth-provider-header">
          <div className="oauth-provider-title">
            <img src="https://www.microsoft.com/favicon.ico" alt="Microsoft"  />
            <h4>Microsoft 365 OAuth</h4>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={config.microsoft.enabled}
              onChange={(e) => setConfig({ ...config, microsoft: { ...config.microsoft, enabled: e.target.checked } })}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Application (Client) ID</label>
            <input
              type="text"
              className="form-input"
              value={config.microsoft.clientId}
              onChange={(e) => setConfig({ ...config, microsoft: { ...config.microsoft, clientId: e.target.value } })}
              placeholder="Azure AD Application ID"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Directory (Tenant) ID</label>
            <input
              type="text"
              className="form-input"
              value={config.microsoft.tenantId}
              onChange={(e) => setConfig({ ...config, microsoft: { ...config.microsoft, tenantId: e.target.value } })}
              placeholder="common (or specific tenant ID)"
            />
            <small className="oauth-form-hint">Use "common" for any Microsoft account, or tenant ID for organization only</small>
          </div>

          <div className="form-group form-group-full">
            <label className="form-label">
              Client Secret {config.microsoft.hasSecret && <span className="config-status">(configured)</span>}
            </label>
            <input
              type="password"
              className="form-input"
              value={microsoftSecret}
              onChange={(e) => setMicrosoftSecret(e.target.value)}
              placeholder={config.microsoft.hasSecret ? '••••••••' : 'Enter client secret'}
            />
          </div>
        </div>

        <div className="oauth-form-actions">
          <button className="btn btn-primary btn-sm" onClick={saveMicrosoftConfig} disabled={saving}>
            Save Microsoft Settings
          </button>
          
          {config.microsoft.enabled && config.microsoft.hasSecret && (
            <button className="btn btn-secondary btn-sm" onClick={connectEmailMicrosoft}>
              <Mail size={14} />
              {config.microsoft.hasRefreshToken ? 'Reconnect' : 'Connect'} Email
            </button>
          )}
          
          {config.microsoft.hasRefreshToken && (
            <span className="email-connected">
              <CheckCircle size={16} /> Email Connected
            </span>
          )}
        </div>

        <div className="oauth-help-links">
          <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="oauth-help-link">
            <ExternalLink size={12} /> Azure App Registrations
          </a>
          <span className="callback-url">
            Callback URL: <code >{window.location.origin}/api/auth/microsoft/callback</code>
          </span>
        </div>
      </div>

      {/* Email Provider Selection */}
      <div className="oauth-provider-card mt-24">
        <h4 className="email-section-title">
          <Mail size={20} />
          Email Sending
        </h4>
        
        <p className="email-section-description">
          Select which OAuth provider to use for sending emails (registration verification, password resets, notifications).
        </p>

        <div className="email-provider-options">
          <label className="email-provider-option">
            <input
              type="radio"
              name="emailProvider"
              checked={config.emailProvider === 'google'}
              onChange={() => setConfig({ ...config, emailProvider: 'google' })}
              disabled={!config.google.hasRefreshToken}
            />
            Google Gmail
            {config.google.hasRefreshToken ? (
              <CheckCircle size={14} className="icon-success" />
            ) : (
              <span className="not-connected">(not connected)</span>
            )}
          </label>

          <label className="email-provider-option">
            <input
              type="radio"
              name="emailProvider"
              checked={config.emailProvider === 'microsoft'}
              onChange={() => setConfig({ ...config, emailProvider: 'microsoft' })}
              disabled={!config.microsoft.hasRefreshToken}
            />
            Microsoft 365
            {config.microsoft.hasRefreshToken ? (
              <CheckCircle size={14} className="icon-success" />
            ) : (
              <span className="not-connected">(not connected)</span>
            )}
          </label>

          <label className="email-provider-option">
            <input
              type="radio"
              name="emailProvider"
              checked={config.emailProvider === 'none'}
              onChange={() => setConfig({ ...config, emailProvider: 'none' })}
            />
            None (SMTP fallback)
          </label>
        </div>

        <div className="oauth-form-actions">
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={testEmail}
            disabled={config.emailProvider === 'none'}
          >
            <Mail size={14} />
            Send Test Email
          </button>
        </div>
      </div>
    </div>
  );
};

export default OAuthSettings;
